import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ApiError } from "../api/client";
import { useCancelTurn, usePatchModel, useSendTurn } from "../api/models";
import type { ModelDetail, ModelSelection } from "../api/types";
import { rowMessageFor } from "../copy/errors";
import { ErrorLine } from "../ui/ErrorLine";
import { UploadButton } from "../library/UploadButton";
import { Button } from "../ui";
import { humanise } from "../viewer/math";
import {
  PHOTOS_MAX,
  PHOTOS_TITLE,
  PROMPT_MAX,
  shortfall,
} from "./NewDesignPage";
import { isBuild } from "./StateLine";
import { estimateLine } from "./format";

export const STOP_COST =
  "Stopping charges for the work done so far (at least 50 credits).";

const LINE = "m-0 text-sm text-slate-700";

/** What is sent of a selection: without the view when no point was picked
 *  (a surface chosen from its row). */
export function sentSelection(selection: ModelSelection): ModelSelection {
  if (selection.point) return selection;
  const sent: ModelSelection = {};
  if (selection.slot) sent.slot = selection.slot;
  if (selection.part) sent.part = selection.part;
  return sent;
}

/** Where the user says what should change ([design-studio §11.7]): the
 *  field, what it is about, reference photos, what it costs, **Send**, and
 *  **Stop** while a step runs. Ctrl+Enter or Cmd+Enter sends; Enter is a new
 *  line, so a stray Enter never spends credits. */
export function InstructionBox(props: {
  detail: ModelDetail;
  /** The organisation's available credits; null when not known. */
  available: number | null;
  selection: ModelSelection | null;
  onClearSelection(): void;
  /** The latest thinking line of the step in progress. */
  thinking: string | null;
  /** One line that grows to four (the phone's sticky box). */
  compact?: boolean;
}): JSX.Element {
  const { detail, available, selection, thinking } = props;
  const { id, latest, current, turn_estimate: estimate } = detail;
  const send = useSendTurn(id);
  const cancel = useCancelTurn(id);
  const patch = usePatchModel(id);
  const [text, setText] = useState("");
  const field = useRef<HTMLTextAreaElement>(null);

  const busy = detail.state !== "idle";
  const running =
    latest !== null &&
    (latest.status === "queued" || latest.status === "running");
  const instruction = latest !== null && !isBuild(latest);
  const ended =
    latest !== null &&
    instruction &&
    (latest.status === "failed" ||
      latest.status === "refused" ||
      // With nothing built yet a stopped first step is tried again too.
      (latest.status === "cancelled" && current === null));

  // A step that did not succeed gives its instruction back, once: what the
  // user types afterwards is theirs.
  const refilled = useRef<number | null>(null);
  const endedN = ended ? latest.n : null;
  const endedText = ended ? (latest.instruction ?? "") : "";
  useEffect(() => {
    if (endedN === null || refilled.current === endedN) return;
    refilled.current = endedN;
    setText(endedText);
  }, [endedN, endedText]);

  // The phone's box is one line and grows with its text, to four.
  useLayoutEffect(() => {
    const el = field.current;
    if (!el || !props.compact) return;
    el.style.height = "auto";
    if (el.scrollHeight > 0) el.style.height = `${el.scrollHeight}px`;
  }, [text, props.compact]);

  // below the estimate of the next instruction it is not sent (§15.1)
  const short =
    available !== null && available < estimate.next.credits
      ? shortfall(available, estimate.next.credits)
      : null;
  const typed = text.trim();
  const blocked = typed.length === 0 || busy || short !== null;

  const onSend = () => {
    if (blocked || send.isPending) return;
    send.mutate(
      {
        instruction: typed,
        ...(selection ? { selection: sentSelection(selection) } : {}),
      },
      {
        onSuccess: () => {
          setText("");
          props.onClearSelection();
        },
      },
    );
  };

  const refs = detail.refs;
  const full = refs.length >= PHOTOS_MAX;
  const refIds = useMemo(() => refs.map((ref) => ref.id), [refs]);
  // A stop that arrives after the end is not an error: the design is asked
  // for again and shows how the step ended.
  const stopError =
    cancel.error instanceof ApiError && cancel.error.code === "invalid_state"
      ? null
      : cancel.error;

  const label =
    current === null ? "Describe the object" : "What should change?";
  const compact = props.compact === true;
  // **Stop** stands in place of **Send** while an instruction is worked on;
  // a build has no Stop.
  const stopping = running && instruction;
  const primary = stopping ? (
    <Button
      type="button"
      className="min-h-[44px] shrink-0 disabled:cursor-not-allowed disabled:opacity-50 md:min-h-0"
      disabled={cancel.isPending}
      aria-busy={cancel.isPending ? true : undefined}
      onClick={() => cancel.mutate(latest.n)}
    >
      Stop
    </Button>
  ) : (
    <Button
      type="button"
      variant="primary"
      className="min-h-[44px] shrink-0 disabled:cursor-not-allowed disabled:opacity-50 md:min-h-0"
      disabled={blocked || send.isPending}
      aria-busy={send.isPending ? true : undefined}
      onClick={onSend}
    >
      {ended && current === null ? "Try again" : "Send"}
    </Button>
  );
  const stopCost = (
    <span
      className={compact ? "text-xs text-slate-600" : "text-sm text-slate-600"}
      data-testid="stop-cost"
    >
      {STOP_COST}
    </span>
  );
  const fieldId = `instruction-${id}`;

  return (
    <section className="flex flex-col gap-2" data-testid="instruction-box">
      {/* What became of the last instruction, until the next one starts.
          With nothing built yet the page says it where the 3D view will be. */}
      {ended && current !== null && latest.status !== "cancelled" ? (
        <div role="alert" className="flex flex-col gap-1" data-testid="outcome">
          <p className={LINE}>{rowMessageFor(latest.error_code)}</p>
          {latest.status === "refused" && latest.refusal_reason ? (
            <p className={LINE}>{latest.refusal_reason}</p>
          ) : null}
        </div>
      ) : null}
      {running && thinking ? (
        <p
          role="status"
          aria-live="polite"
          className="m-0 text-sm text-slate-600"
          data-testid="thinking"
        >
          {thinking}
        </p>
      ) : null}
      {short ? (
        <ErrorLine error={short} context="instruction" />
      ) : compact && running ? null : (
        // On a phone the line makes room for the step in progress: nothing
        // can be sent until it has ended.
        <p
          className={compact ? "m-0 text-xs text-slate-600" : LINE}
          data-testid="estimate-line"
        >
          {estimateLine(
            current === null ? estimate.first : estimate.next,
            estimate.hold,
          )}
        </p>
      )}
      <label
        htmlFor={fieldId}
        className="tb-field-label"
        data-testid="instruction-label"
      >
        {label}
      </label>
      {selection?.slot ? (
        <button
          type="button"
          className="inline-flex min-h-[44px] items-center gap-1 self-start rounded-pill bg-brand-tint px-3 py-1 text-sm font-semibold text-brand-dark md:min-h-0"
          aria-label={`About the surface ${humanise(selection.slot)}. Remove`}
          data-testid="selection-chip"
          onClick={props.onClearSelection}
        >
          About: {humanise(selection.slot)} ✕
        </button>
      ) : null}
      <div className={compact ? "flex items-end gap-2" : "flex flex-col"}>
        <textarea
          id={fieldId}
          ref={field}
          className={[
            "tb-input min-w-0 flex-1",
            compact
              ? "max-h-[6.5rem] min-h-[44px] resize-none"
              : "min-h-[84px] resize-y",
          ].join(" ")}
          rows={compact ? 1 : 3}
          value={text}
          maxLength={PROMPT_MAX}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
              event.preventDefault();
              onSend();
            }
          }}
        />
        {compact ? primary : null}
      </div>
      {compact && stopping ? stopCost : null}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 max-md:[&_.tb-btn]:min-h-[44px]">
        {compact ? null : primary}
        {compact || !stopping ? null : stopCost}
        <UploadButton
          label="Upload an image"
          size="sm"
          testId="ref-input"
          disabled={full || patch.isPending}
          title={full ? PHOTOS_TITLE : undefined}
          onUploaded={(uploadId) =>
            patch.mutate({ refs: [...refIds, uploadId] })
          }
        />
        <span className="text-xs text-slate-500">Ctrl+Enter to send</span>
      </div>
      {refs.length > 0 ? (
        <ul
          className="m-0 flex list-none flex-wrap gap-2 p-0"
          aria-label="Reference photos"
          data-testid="refs"
        >
          {refs.map((ref) => (
            <li key={ref.id}>
              <img
                src={ref.url}
                alt="Reference photo"
                loading="lazy"
                className="block h-12 w-12 rounded-md bg-slate-100 object-cover"
              />
            </li>
          ))}
        </ul>
      ) : null}
      {send.isError ? (
        <ErrorLine error={send.error} context="instruction" />
      ) : null}
      {stopError ? <ErrorLine error={stopError} context="design" /> : null}
      {patch.isError ? (
        <ErrorLine error={patch.error} context="design" />
      ) : null}
    </section>
  );
}
